// WAAP Access Control — website WhiteList / Blacklist / Token Secret Access.
//
// MIGRATED to the PUBLIC Open-API surface (root conversant SDK, HMAC). Unlike the
// private SSO portal (`/access-control` returning `{ rules: [...] }`),
// the public surface is a per-website rule collection:
//   GET    /waap/domains/:domainId/access-control            → AccessControlRule[]
//   POST   /waap/domains/:domainId/access-control            → create one rule
//   PUT    /waap/domains/:domainId/access-control/:ruleId    → replace one rule
//   DELETE /waap/domains/:domainId/access-control/:ruleId    → delete one rule
// :domainId anchors the website; the BE resolves the vendor service from it.
//
// The public DTO field names diverge from the private one (see _shared.ts
// WaapAccessControl): match `what`→`field`, `patterns`→`values`; `subnet`→
// `ip_restriction`, `location`→`geo_restriction`. We therefore define NEW local
// types here (private types in _shared.ts are left untouched). The list endpoint
// returns a BARE JSON array (not `{ rules }`); create/update echo the saved rule;
// delete returns `{ status: "ok" }`. There is NO PATCH toggle — enable/disable is
// done with a PUT of the full body with `enabled` flipped.
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';

// ---- Public Open-API DTOs (mirror the BE publicAccessControlDTO) ----------

/** One match condition on a public access-control rule (e.g. field `req.path`,
 *  operator `equals`, values `["/x"]`). */
export interface WaapAccessControlMatch {
  field: string;
  operator: string;
  values: string[];
}

/** A website access-control rule on the public Open-API surface. `type` selects
 *  the section: allow (WhiteList) | deny (Blacklist) | token (Token Secret). */
export interface WaapAccessControlRule {
  id: number;
  name: string;
  type: string;
  matches: WaapAccessControlMatch[];
  priority: number;
  /** IP / Network restriction (comma-separated CIDR list). */
  ip_restriction: string;
  /** GEO restriction (comma-separated ISO codes). */
  geo_restriction: string;
  /** Blacklist: apply to anonymous IPs. */
  anonymous_ip: boolean;
  /** Token Secret Access secret. */
  token_secret: string;
  enabled: boolean;
}

/** Create/update body — same shape as the rule minus the server-assigned id. */
export type WaapAccessControlInput = Omit<WaapAccessControlRule, 'id'>;

// ---- paths + keys --------------------------------------------------------

const acPath = (domainId: string, sub = '') =>
  `/waap/domains/${domainId}/access-control${sub}`;

export const accessControlKeys = {
  all: ['waap', 'access-control'] as const,
  rules: (domainId: string) => ['waap', 'access-control', domainId] as const,
};

/** Tolerate a bare array (public) or a `{ data: [...] }` envelope. */
function asArray<T = unknown>(raw: unknown): T[] {
  if (Array.isArray(raw)) return raw as T[];
  if (raw && typeof raw === 'object') {
    const v = (raw as Record<string, unknown>).data;
    if (Array.isArray(v)) return v as T[];
  }
  return [];
}

/** Strip the server-assigned id to build a write body from an existing rule. */
const toInput = (r: WaapAccessControlRule): WaapAccessControlInput => {
  const { id: _id, ...rest } = r;
  void _id;
  return rest;
};

// ---- hooks ---------------------------------------------------------------

/** List a website's access-control rules ([] on 400/404 = none/unconfigured). */
export function useWaapAccessControls(domainId: string | undefined) {
  return useQuery({
    queryKey: accessControlKeys.rules(domainId ?? ''),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<WaapAccessControlRule[]> => {
      try {
        const res = await serviceApiV3.get<unknown>(acPath(domainId as string));
        return asArray<WaapAccessControlRule>(res.data);
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404) return [];
        throw e;
      }
    },
  });
}

/** Create one access-control rule (allow/deny/token). Invalidates the list. */
export function useCreateWaapAccessControl(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, WaapAccessControlInput>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: WaapAccessControlInput) => {
      const res = await serviceApiV3.post(acPath(domainId), body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: accessControlKeys.rules(domainId) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Replace one access-control rule (PUT /:ruleId). Invalidates the list. */
export function useUpdateWaapAccessControl(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, { id: number; body: WaapAccessControlInput }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: WaapAccessControlInput }) => {
      const res = await serviceApiV3.put(acPath(domainId, `/${id}`), body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: accessControlKeys.rules(domainId) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Enable/disable one rule. The public surface has no PATCH toggle, so we PUT
 *  the full rule body with `enabled` flipped. Invalidates the list. */
export function useToggleWaapAccessControl(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, { rule: WaapAccessControlRule; enabled: boolean }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ rule, enabled }: { rule: WaapAccessControlRule; enabled: boolean }) => {
      const res = await serviceApiV3.put(acPath(domainId, `/${rule.id}`), {
        ...toInput(rule),
        enabled,
      });
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: accessControlKeys.rules(domainId) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Delete one access-control rule (DELETE /:ruleId). Invalidates the list. */
export function useDeleteWaapAccessControl(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, number>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await serviceApiV3.delete(acPath(domainId, `/${id}`));
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: accessControlKeys.rules(domainId) });
      options?.onSuccess?.(...args);
    },
  });
}
